import { useEffect, useState, useCallback } from "react";
import { Container, Button, Form, Spinner, Alert } from "react-bootstrap";
import { Link } from "react-router-dom";

const PAGE_SIZE = 24;

function formatDisplayDate(isoDate) {
  if (!isoDate || typeof isoDate !== "string") return "—";
  const [y, m, d] = isoDate.split("-").map(Number);
  if (!y || !m || !d) return isoDate;
  const months = [
    "JAN", "FEB", "MAR", "APR", "MAY", "JUN",
    "JUL", "AUG", "SEP", "OCT", "NOV", "DEC",
  ];
  return `${months[m - 1] || ""} ${d}, ${y}`;
}

export default function PublicBrowse() {
  const [days, setDays] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [sort, setSort] = useState("date_desc");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchPage = useCallback(async (p, sortVal) => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams({
        page: String(p),
        limit: String(PAGE_SIZE),
        sort: sortVal,
      });
      const res = await fetch(`/api/days/public-registry?${params.toString()}`);
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || "Failed to load registry");
      }
      const data = await res.json();
      setDays(Array.isArray(data.days) ? data.days : []);
      setPage(data.page || p);
      setTotalPages(data.totalPages || 1);
      setTotal(typeof data.total === "number" ? data.total : 0);
    } catch (e) {
      setError(e.message || "Something went wrong");
      setDays([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPage(1, sort);
  }, [sort, fetchPage]);

  const goPrev = () => {
    if (page <= 1) return;
    fetchPage(page - 1, sort);
  };

  const goNext = () => {
    if (page >= totalPages) return;
    fetchPage(page + 1, sort);
  };

  return (
    <div className="brutal-landing py-4 py-md-5">
      <Container>
        <div className="brutal-section-box mb-4" style={{ maxWidth: "960px", margin: "0 auto" }}>
          <h1 className="brutal-heading-black mb-2" style={{ fontSize: "clamp(22px, 5vw, 36px)" }}>
            PUBLIC REGISTRY
          </h1>
          <p className="brutal-text mb-3" style={{ textTransform: "none", fontWeight: 600 }}>
            DAYS CLAIMED BY THE COMMUNITY — TITLES AND DESCRIPTIONS. NO ACCOUNT REQUIRED.
          </p>
          <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
            <Button as={Link} to="/login" className="brutal-btn brutal-btn-primary" size="sm">
              LOGIN TO BUY
            </Button>
            <Button as={Link} to="/register" className="brutal-btn brutal-btn-secondary" size="sm">
              REGISTER
            </Button>
            <span className="brutal-text" style={{ fontSize: "11px", opacity: 0.85 }}>
              FULL CALENDAR &amp; PURCHASES REQUIRE AN ACCOUNT.
            </span>
          </div>
        </div>

        <div
          className="d-flex flex-column flex-md-row gap-3 justify-content-between align-items-stretch align-items-md-center mb-3"
          style={{ maxWidth: "1100px", margin: "0 auto" }}
        >
          <div className="d-flex align-items-center gap-2">
            <span className="brutal-subheading mb-0" style={{ fontSize: "12px" }}>
              SORT
            </span>
            <Form.Select
              size="sm"
              className="brutal-form-control"
              style={{ maxWidth: "220px" }}
              value={sort}
              onChange={(e) => setSort(e.target.value)}
            >
              <option value="date_desc">NEWEST DATES FIRST</option>
              <option value="date">OLDEST DATES FIRST</option>
            </Form.Select>
          </div>
          <div className="brutal-text" style={{ fontSize: "12px" }}>
            {total > 0 ? (
              <>
                SHOWING {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} OF {total}
              </>
            ) : (
              <>NO DAYS YET</>
            )}
          </div>
        </div>

        {error && (
          <Alert variant="danger" className="brutal-alert mb-3" style={{ maxWidth: "1100px", margin: "0 auto" }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" role="status" style={{ borderWidth: "4px" }} />
            <div className="brutal-text mt-3">LOADING…</div>
          </div>
        ) : (
          <div className="public-browse-grid" style={{ maxWidth: "1100px", margin: "0 auto" }}>
            {days.length === 0 && !error ? (
              <p className="brutal-text text-center py-5">NO PURCHASED DAYS TO SHOW YET.</p>
            ) : (
              days.map((day) => (
                <article
                  key={day._id}
                  className="public-browse-card"
                  style={{
                    border: "4px solid #000000",
                    background: "#FFFFFF",
                    padding: "16px",
                    boxShadow: "6px 6px 0px #000000",
                  }}
                >
                  <div className="d-flex justify-content-between align-items-start gap-2 mb-2 flex-wrap">
                    <div className="brutal-subheading mb-0" style={{ fontSize: "13px", color: "#0000FF" }}>
                      {formatDisplayDate(day.date)}
                    </div>
                    <div className="brutal-text" style={{ fontSize: "11px", fontWeight: 800 }}>
                      OWNER: {day.owner?.displayName || "UNKNOWN"}
                    </div>
                  </div>
                  <h2 className="brutal-subheading mb-2" style={{ fontSize: "16px", textTransform: "none" }}>
                    {day.title?.trim() ? day.title : "UNTITLED DAY"}
                  </h2>
                  {day.description?.trim() ? (
                    <p className="brutal-text mb-2" style={{ textTransform: "none", fontSize: "13px", lineHeight: 1.5 }}>
                      {day.description}
                    </p>
                  ) : (
                    <p className="brutal-text mb-2" style={{ fontSize: "12px", opacity: 0.7 }}>
                      NO DESCRIPTION.
                    </p>
                  )}
                </article>
              ))
            )}
          </div>
        )}

        {!loading && totalPages > 1 && (
          <div className="d-flex justify-content-center align-items-center gap-2 mt-4 flex-wrap">
            <Button
              className="brutal-btn brutal-btn-secondary"
              size="sm"
              disabled={page <= 1}
              onClick={goPrev}
            >
              PREVIOUS
            </Button>
            <span className="brutal-text" style={{ fontSize: "12px" }}>
              PAGE {page} / {totalPages}
            </span>
            <Button
              className="brutal-btn brutal-btn-secondary"
              size="sm"
              disabled={page >= totalPages}
              onClick={goNext}
            >
              NEXT
            </Button>
          </div>
        )}
      </Container>
      <style>{`
        .public-browse-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 20px;
        }
        @media (min-width: 768px) {
          .public-browse-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }
      `}</style>
    </div>
  );
}
